// Client-only split-screen orchestration. No HTTP — mirrors the `maps` mock module.
// `tiles` is the single source of truth for what's on the canvas: a map is visible
// iff it's a tile. The eye toggle and the directional Split submenu both mutate it.

import { toast } from '@/components/ui/toast/use-toast'

const MAX_TILES = 4
const EDGE_FIRST = new Set(['left', 'top'])   // placed map takes the first slot
const VERTICAL = new Set(['top', 'bottom'])   // top/bottom => stacked (arrangement 'tb')

// Default arrangement for an auto-tiled grid of `count` maps (no explicit direction).
const arrangementFor = (count) => (count >= 4 ? 'quad' : count === 3 ? 'left' : 'lr')

const tooManyToast = () => toast({ title: 'You can show up to 4 maps at once' })

// Seed the canvas with the Default Map once, so the first map is visible by default.
// The `initialized` guard means a user who hides every map keeps a blank canvas
// instead of the default reappearing.
export const ensureTiles = ({ commit, getters, rootGetters }, { orgId }) => {
  if (getters.isInitialized(orgId)) return
  const def = rootGetters['maps/getDefaultMap'](orgId)
  if (!def) return
  commit('SET_TILES', { orgId, tiles: [def.id] })
  commit('SET_INITIALIZED', { orgId })
}

// Add a map to the canvas (eye on). Auto-tiles with a sensible default arrangement.
export const showMap = ({ commit, getters }, { orgId, mapId }) => {
  const tiles = getters.getTiles(orgId)
  if (tiles.includes(mapId)) return
  if (tiles.length >= MAX_TILES) { tooManyToast(); return }
  const newTiles = [...tiles, mapId]
  commit('SET_ARRANGEMENT', { orgId, arrangement: arrangementFor(newTiles.length) })
  commit('SET_TILES', { orgId, tiles: newTiles })
}

// Remove a map from the canvas (eye off / tile ✕). Unlike the old collapse, a single
// remaining map stays as one tile, and zero maps leaves a blank canvas.
export const hideMap = ({ commit, getters }, { orgId, mapId }) => {
  const remaining = getters.getTiles(orgId).filter(id => id !== mapId)
  if (remaining.length === 2) {
    const arr = getters.getArrangement(orgId)
    commit('SET_ARRANGEMENT', { orgId, arrangement: arr === 'tb' ? 'tb' : 'lr' })
  }
  commit('SET_TILES', { orgId, tiles: remaining })
}

export const toggleMap = ({ dispatch, getters }, { orgId, mapId }) => {
  if (getters.getTiles(orgId).includes(mapId)) dispatch('hideMap', { orgId, mapId })
  else dispatch('showMap', { orgId, mapId })
}

// ✕ on a tile — same as turning its eye off.
export const removeTileMap = ({ dispatch }, { orgId, mapId }) => {
  dispatch('hideMap', { orgId, mapId })
}

// Place a map into the split from its sidebar menu, at the slot implied by direction.
//  - direction picks the arrangement AND which slot the placed map takes.
//  - Adding to a single tile makes a 2-up; a 3rd becomes a "master" layout where the
//    placed map takes the full edge; a 4th switches to a 2×2 quad.
export const placeInSplit = ({ commit, getters }, { orgId, mapId, direction }) => {
  const tiles = getters.getTiles(orgId)
  if (tiles.length >= MAX_TILES && !tiles.includes(mapId)) { tooManyToast(); return }
  const others = tiles.filter(id => id !== mapId)
  const first = EDGE_FIRST.has(direction)

  if (others.length === 0) {
    // Nothing else on the canvas yet — just show this map full-bleed.
    commit('SET_TILES', { orgId, tiles: [mapId] })
    return
  }

  let arrangement
  let newTiles
  if (others.length === 1) {
    arrangement = VERTICAL.has(direction) ? 'tb' : 'lr'
    newTiles = first ? [mapId, others[0]] : [others[0], mapId]
  } else if (others.length === 2) {
    // 3-up master: the direction is the arrangement; placed map is the master.
    arrangement = direction
    newTiles = first ? [mapId, ...others] : [...others, mapId]
  } else {
    arrangement = 'quad'
    newTiles = (first ? [mapId, ...others] : [...others, mapId]).slice(0, MAX_TILES)
  }

  commit('SET_ARRANGEMENT', { orgId, arrangement })
  commit('SET_TILES', { orgId, tiles: newTiles })
}

export const setDividers = ({ commit }, payload) => {
  commit('SET_DIVIDERS', payload)
}

export const toggleSync = ({ commit, getters }, { mapId }) => {
  commit('SET_SYNC', { mapId, enabled: !getters.isSyncEnabled(mapId) })
}

// Focus a tile so global map interactions (layer-create drawing, search) target it.
export const setFocusedTile = ({ commit }, { mapId }) => {
  commit('SET_FOCUSED_TILE', { mapId: mapId || null })
}
